/**
 * CRM activity-export wire access + a display-ready read model of the call→CRM
 * field mapping. Provider differences (Salesforce's flat row list bucketed by
 * vocabulary group vs HubSpot's per-activity lists, `value` vs `key` vocab ids,
 * where option labels live) end at the adapters here — consumers see one shape.
 *
 * Mapping EDITING (autosave, suggestion seeding, picklist loading) stays in the
 * Integrations drawer's ExportTab; the one write here is the master enable
 * switch, which /settings/phone flips for dialer-only admins.
 */
import { apiFetch } from './api'

export type CrmExportProvider = 'salesforce' | 'hubspot'

export interface CrmExportUser {
  name?: string
  email?: string
  crm_user_id?: string | null
}

export function crmUserLabel(u: CrmExportUser | null | undefined): string {
  const who = u?.name || u?.email || ''
  return who && u?.crm_user_id ? `${who} (${u.crm_user_id})` : who
}

export interface CrmExportSettings {
  crm_export_enabled: boolean
  provider: CrmExportProvider | null
  connection_user: CrmExportUser | null
}

export async function getCrmExportSettings(): Promise<CrmExportSettings> {
  const r = await apiFetch('/settings/crm_export')
  const provider = r?.provider === 'salesforce' || r?.provider === 'hubspot' ? r.provider : null
  return {
    crm_export_enabled: Boolean(r?.crm_export_enabled),
    provider,
    connection_user: r?.connection_user ?? null,
  }
}

// The PUT response carries no provider/credits — callers re-GET afterwards
// (useCrmExport.setEnabled does).
export async function putCrmExportEnabled(enabled: boolean): Promise<void> {
  await apiFetch('/settings/crm_export', { method: 'PUT', body: { crm_export_enabled: enabled } })
}

// ---------------------------------------------------------------------------
// Call-mapping snapshot
// ---------------------------------------------------------------------------

export interface MappingValuePair {
  code: string
  from: string
  to: string | null // null → not mapped
}

export interface MappingRowView {
  sourceField: string
  crmField: string
  sourceLabel: string
  crmFieldLabel: string
  valuePairs: MappingValuePair[] | null
}

export interface CallMappingSnapshot {
  usingDefaults: boolean
  rows: MappingRowView[]
}

export interface SourceFieldMeta {
  id: string
  label: string
  values?: { code: string; label: string }[]
}

export interface CrmFieldMeta {
  name: string
  label?: string
  options?: { value: string; label: string }[]
}

interface RawMappingRow {
  source_field: string
  crm_field: string
  value_map?: Record<string, string> | null
}

/**
 * Pure normalizer. Any enum-typed source row (its meta carries `values`) with at
 * least one mapped value gets a full valuePairs enumeration.
 */
export function buildRows(
  rawRows: RawMappingRow[],
  sourceMetas: SourceFieldMeta[],
  crmMetas: CrmFieldMeta[],
): MappingRowView[] {
  return rawRows
    .filter((r) => r.source_field && r.crm_field)
    .map((r) => {
      const source = sourceMetas.find((s) => s.id === r.source_field)
      const crm = crmMetas.find((c) => c.name === r.crm_field)
      return {
        sourceField: r.source_field,
        crmField: r.crm_field,
        sourceLabel: source?.label || r.source_field,
        crmFieldLabel: crm?.label || r.crm_field,
        valuePairs: buildValuePairs(r.value_map, source, crm),
      }
    })
}

function buildValuePairs(
  valueMap: Record<string, string> | null | undefined,
  source: SourceFieldMeta | undefined,
  crm: CrmFieldMeta | undefined,
): MappingValuePair[] | null {
  const vocabValues = source?.values
  if (!vocabValues?.length || !valueMap || !Object.keys(valueMap).length) return null
  const toLabel = (v: string) => crm?.options?.find((o) => o.value === v)?.label || v
  const pairs: MappingValuePair[] = vocabValues.map((v) => ({
    code: v.code,
    from: v.label,
    to: valueMap[v.code] ? toLabel(valueMap[v.code]) : null,
  }))
  const known = new Set(vocabValues.map((v) => v.code))
  for (const [code, v] of Object.entries(valueMap)) {
    if (!known.has(code) && v) pairs.push({ code, from: code, to: toLabel(v) })
  }
  return pairs
}

/**
 * Fetch + normalize the provider's call-scope mapping. The mappings fetch
 * failing throws (the caller's error state); vocabulary and describe are label
 * sugar and fail soft — API names still tell the truth.
 */
export async function getCallMappingSnapshot(provider: CrmExportProvider): Promise<CallMappingSnapshot> {
  return provider === 'salesforce' ? sfSnapshot() : hsSnapshot()
}

async function sfSnapshot(): Promise<CallMappingSnapshot> {
  const [maps, voc, desc] = await Promise.all([
    apiFetch('/crm/salesforce/activity-field-mappings'),
    apiFetch('/crm/salesforce/activity-source-vocabulary').catch(() => null),
    apiFetch('/crm/salesforce/describe?object=task').catch(() => null),
  ])
  const vocab: { value: string; label: string; group: string; values?: { code: string; label: string }[] }[] =
    voc?.fields || []
  const sourceMetas: SourceFieldMeta[] = vocab.map((f) => ({ id: f.value, label: f.label, values: f.values }))
  // SF stores call + email rows as one flat Task mapping; keep the call bucket
  // (vocab group, with a key-prefix fallback for keys the vocab no longer knows).
  const isCall = (sourceField: string) => {
    const v = vocab.find((f) => f.value === sourceField)
    return v ? v.group === 'Call' : !sourceField.startsWith('Email')
  }
  const rawRows = ((maps?.rows || []) as RawMappingRow[]).filter((r) => isCall(r.source_field))
  return {
    usingDefaults: Boolean(maps?.defaults),
    rows: buildRows(rawRows, sourceMetas, desc?.fields || []),
  }
}

async function hsSnapshot(): Promise<CallMappingSnapshot> {
  const [maps, voc, desc] = await Promise.all([
    apiFetch('/crm/hubspot/activity-field-mappings'),
    apiFetch('/crm/hubspot/activity-source-vocabulary?activity_type=call').catch(() => null),
    apiFetch('/crm/hubspot/describe?object=calls').catch(() => null),
  ])
  const vocab: { key: string; label: string; values?: { code: string; label: string }[] }[] = voc?.fields || []
  const sourceMetas: SourceFieldMeta[] = vocab.map((f) => ({ id: f.key, label: f.label, values: f.values }))
  return {
    usingDefaults: false,
    rows: buildRows((maps?.mappings?.call || []) as RawMappingRow[], sourceMetas, desc?.fields || []),
  }
}
